/**
 * App-wide banner shown when the current org has been scheduled for
 * deletion. Mounts in :file:`AppShell.tsx` above the routed content so
 * every authenticated page shows the same warning.
 *
 * Data source is the ``/organizations/current`` endpoint, not the auth
 * payload — auth tokens are cached and we want this warning to appear
 * the same session the SUPER_ADMIN schedules the deletion. We fetch
 * once on mount and then not again; admins who scheduled the deletion
 * get the immediate update from the Settings page itself, and
 * non-admins only need to know "yes, this org is ending" which a single
 * per-session fetch delivers correctly.
 */
import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { getCurrentOrg } from "@/api/organizations";
import type { Organization } from "@/types/organization";

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "the scheduled date";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export function PendingDeletionBanner() {
  const [org, setOrg] = useState<Organization | null>(null);

  useEffect(() => {
    // Best-effort — if the call fails (logged-out state, network blip) we
    // silently render nothing rather than blocking the UI.
    getCurrentOrg()
      .then(setOrg)
      .catch(() => setOrg(null));
  }, []);

  if (!org) return null;
  if (org.status !== "PENDING_DELETION") return null;

  const date = formatDate(org.deletion_scheduled_for ?? null);

  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-lg border border-danger/30 bg-danger/10 px-4 py-3 mb-4"
    >
      <AlertTriangle className="h-5 w-5 text-danger shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-sm font-medium text-text-primary">
          This organization is scheduled for deletion on {date}.
        </p>
        <p className="text-xs text-text-muted mt-0.5">
          All data will be permanently removed. Contact{" "}
          <a
            href="mailto:support@lenavio.com"
            className="text-accent hover:underline"
          >
            support@lenavio.com
          </a>{" "}
          to cancel.
        </p>
      </div>
    </div>
  );
}
